import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Camera, Calendar, FileText, Star, CheckCircle, XCircle, AlertCircle, Trash2, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import EarningsReport from '../components/EarningsReport';
import TimeOffRequestModal from '../components/TimeOffRequestModal';
import ResourcePaystubsModal from '../components/ResourcePaystubsModal';
import { format } from 'date-fns';

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  role?: string;
  resourceId?: string;
  avatarUrl?: string;
}

interface TimeOffRequest {
  id: string;
  start_date: string;
  end_date: string;
  request_type: string;
  is_partial_day: boolean;
  start_time?: string;
  end_time?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  approved_by?: string;
  approved_at?: string;
  reason_for_denial?: string;
  business_id: string;
  resource_id: string;
}

interface StarRatingProps {
  rating: number;
  size?: 'small' | 'medium' | 'large';
  showValue?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({ rating, size = 'medium', showValue = false }) => {
  const sizeClasses = {
    small: 'h-3.5 w-3.5',
    medium: 'h-4 w-4',
    large: 'h-5 w-5'
  };

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center space-x-0.5">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= fullStars
                ? 'text-yellow-400 fill-current'
                : star === fullStars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      {showValue && (
        <span className="text-sm font-medium text-gray-600 ml-1">{rating.toFixed(1)}</span>
      )}
    </div>
  );
};

export default function Profile() {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [resourceRating, setResourceRating] = useState<{
    rating: number;
    feedbackCount: number;
  } | null>(null);
  const [isLoadingRating, setIsLoadingRating] = useState(false);
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [isPaystubsModalOpen, setIsPaystubsModalOpen] = useState(false);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [isLoadingTimeOff, setIsLoadingTimeOff] = useState(false);
  const [timeOffDisplayLimit, setTimeOffDisplayLimit] = useState(5);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const { data: { session } } = await supabase!.auth.getSession();
        if (!session?.user) return;

        const { data: businessData } = await supabase!
          .from('businesses')
          .select('id')
          .eq('owner_id', session.user.id)
          .maybeSingle();

        if (businessData) {
          setUserInfo({
            firstName: 'ADMIN',
            lastName: '',
            email: session.user.email || '',
            isAdmin: true
          });
          return;
        }

        const { data: resourceData, error: resourceError } = await supabase!
          .from('resources')
          .select(`
            *,
            role:resource_roles(
              id,
              name,
              description
            )
          `)
          .eq('auth_user_id', session.user.id)
          .single();

        if (resourceError) {
          console.error('Error getting resource:', resourceError);
          return;
        }

        if (resourceData) {
          setUserInfo({
            firstName: resourceData.first_name || '',
            lastName: resourceData.last_name || '',
            email: resourceData.email || '',
            isAdmin: false,
            role: resourceData.role?.name || resourceData.role || 'Resource',
            resourceId: resourceData.id,
            avatarUrl: resourceData.avatar_url
          });
        }
      } catch (error) {
        console.error('Error loading user info:', error);
      }
    };

    loadUserInfo();
  }, []);

  // Resource profiles moved to the Resource Control Centre — send them there.
  useEffect(() => {
    if (userInfo && !userInfo.isAdmin) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    if (userInfo?.resourceId && selectedBusiness?.id && !userInfo.isAdmin) {
      loadResourceRating();
      loadTimeOffRequests();

      if (!supabase) return;
      
      const timeOffSubscription = supabase
        .channel('profile_time_off_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'time_off_requests',
            filter: `resource_id=eq.${userInfo.resourceId}`
          },
          () => {
            loadTimeOffRequests();
          }
        )
        .subscribe();

      return () => {
        if (supabase) {
          supabase.removeChannel(timeOffSubscription);
        }
      };
    }
  }, [userInfo?.resourceId, selectedBusiness?.id]);

  const loadResourceRating = async () => {
    if (!userInfo?.resourceId || !selectedBusiness?.id || !supabase) return;
    
    setIsLoadingRating(true);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startDate = thirtyDaysAgo.toISOString();
      const endDate = new Date().toISOString();

      const { data: feedbackData, error } = await supabase
        .from('booking_feedback')
        .select(`
          *,
          bookings!inner(
            id,
            business_id,
            booking_resources!inner(
              resource_id
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('bookings.booking_resources.resource_id', userInfo.resourceId)
        .eq('status', 'submitted')
        .gte('submitted_at', startDate)
        .lte('submitted_at', endDate);

      if (error) {
        console.error('Error fetching resource rating:', error);
        return;
      }

      if (!feedbackData || feedbackData.length === 0) {
        setResourceRating({ rating: 0, feedbackCount: 0 });
        return;
      }

      let totalRating = 0;
      let totalRatingCount = 0;

      feedbackData.forEach(feedback => {
        let feedbackAverage = 0;
        let ratingCount = 0;

        if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
          Object.values(feedback.form_responses).forEach(value => {
            if (typeof value === 'number' && value > 0) {
              feedbackAverage += value;
              ratingCount++;
            }
          });
        } else {
          if (feedback.overall_quality_rating) {
            feedbackAverage += feedback.overall_quality_rating;
            ratingCount++;
          }
          if (feedback.overall_experience_rating) {
            feedbackAverage += feedback.overall_experience_rating;
            ratingCount++;
          }
        }

        if (ratingCount > 0) {
          totalRating += feedbackAverage / ratingCount;
          totalRatingCount++;
        }
      });

      const averageRating = totalRatingCount > 0 ? totalRating / totalRatingCount : 0;
      setResourceRating({
        rating: averageRating,
        feedbackCount: feedbackData.length
      });

    } catch (error) {
      console.error('Error loading resource rating:', error);
    } finally {
      setIsLoadingRating(false);
    }
  };

  const loadTimeOffRequests = async () => {
    if (!userInfo?.resourceId || !selectedBusiness?.id || !supabase) return;

    setIsLoadingTimeOff(true);
    try {
      const { data: requests, error } = await supabase
        .from('time_off_requests')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('resource_id', userInfo.resourceId)
        .order('requested_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error fetching time off requests:', error);
        return;
      }
        
      setTimeOffRequests(requests || []);
    } catch (error) {
      console.error('Error loading time off requests:', error);
    } finally {
      setIsLoadingTimeOff(false);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('time_off_requests')
        .delete()
        .eq('id', requestId)
        .eq('resource_id', userInfo?.resourceId);

      if (error) {
        console.error('Error canceling time off request:', error);
        return;
      }

      loadTimeOffRequests();
    } catch (error) {
      console.error('Error canceling time off request:', error);
    }
  };

  const handleProfilePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !supabase || !userInfo?.resourceId) return;

    try {
      setIsUploading(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${userInfo.resourceId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('resources')
        .update({ avatar_url: publicUrl })
        .eq('id', userInfo.resourceId);

      if (updateError) throw updateError;

      setUserInfo(prev => prev ? { ...prev, avatarUrl: publicUrl } : null);
    } catch (error) {
      console.error('Error uploading profile photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'approved':
        return { icon: CheckCircle, label: 'Approved', textColor: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-100' };
      case 'denied':
        return { icon: XCircle, label: 'Denied', textColor: 'text-red-500', bgColor: 'bg-red-50', borderColor: 'border-red-100' };
      default:
        return { icon: AlertCircle, label: 'Pending', textColor: 'text-orange-500', bgColor: 'bg-orange-50', borderColor: 'border-orange-100' };
    }
  };

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      vacation: 'Vacation',
      sick: 'Sick Leave',
      personal: 'Personal Day',
      family: 'Family Emergency',
      bereavement: 'Bereavement',
      other: 'Other'
    };
    return typeMap[type] || type;
  };

  const formatRequestDate = (dateStr: string) => {
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      return format(localDate, 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (!userInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F2F2F7]" style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
      }}>
        <div className="text-center">
          <div className="w-8 h-8 border-[3px] border-gray-300 border-t-[#007AFF] rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-[15px] text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#F2F2F7] overflow-y-auto" style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
    }}>
      {/* iOS-style Navigation Bar */}
      <div className="bg-[#F2F2F7] sticky top-0 z-40 px-4 pt-2 pb-1">
        <h1 className="text-[34px] font-bold text-black tracking-tight leading-tight">Profile</h1>
      </div>

      <div className="flex-1 px-4 pb-8">
        {/* Profile Card */}
        <div className="mt-2 bg-white rounded-xl overflow-hidden shadow-sm">
          <div className="px-5 pt-6 pb-5">
            <div className="flex flex-col items-center">
              {/* Avatar */}
              <label className="relative cursor-pointer group mb-3">
                <div className="h-[88px] w-[88px] rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center overflow-hidden shadow-md">
                  {userInfo.avatarUrl ? (
                    <img 
                      src={userInfo.avatarUrl} 
                      alt="Profile" 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-10 w-10 text-white" />
                  )}
                </div>
                {!userInfo.isAdmin && (
                  <>
                    <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity">
                      <Camera className="h-6 w-6 text-white" />
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleProfilePhotoUpload}
                      disabled={isUploading}
                    />
                  </>
                )}
                {isUploading && (
                  <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </div>
                )}
              </label>

              {/* Name */}
              <h2 className="text-[22px] font-semibold text-black">
                {userInfo.isAdmin ? 'ADMIN' : `${userInfo.firstName} ${userInfo.lastName}`}
              </h2>

              {/* Role */}
              <p className="text-[15px] text-gray-500 mt-0.5">
                {userInfo.isAdmin ? 'Administrator' : userInfo.role || 'Resource'}
              </p>

              {/* Star Rating */}
              {!userInfo.isAdmin && (
                <div className="mt-2">
                  {isLoadingRating ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-200 border-t-[#007AFF] rounded-full animate-spin"></div>
                      <span className="text-[13px] text-gray-400">Loading rating...</span>
                    </div>
                  ) : resourceRating && resourceRating.feedbackCount > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <StarRating rating={resourceRating.rating} size="small" showValue />
                      <span className="text-[13px] text-gray-400">
                        ({resourceRating.feedbackCount} review{resourceRating.feedbackCount !== 1 ? 's' : ''})
                      </span>
                    </div>
                  ) : resourceRating && resourceRating.feedbackCount === 0 ? (
                    <p className="text-[13px] text-gray-400">No reviews in last 30 days</p>
                  ) : null}
                </div>
              )}

              {/* Email */}
              <div className="flex items-center gap-1.5 mt-2">
                <Mail className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-[13px] text-gray-500 break-all">{userInfo.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - iOS grouped list style */}
        {!userInfo.isAdmin && (
          <div className="mt-8">
            <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-1.5">Quick Actions</p>
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              {/* Request Time Off */}
              <button
                onClick={() => setIsTimeOffModalOpen(true)}
                className="w-full flex items-center px-4 py-3 active:bg-gray-100 transition-colors"
              >
                <div className="h-[30px] w-[30px] rounded-[7px] bg-[#007AFF] flex items-center justify-center mr-3 flex-shrink-0">
                  <Calendar className="h-[17px] w-[17px] text-white" />
                </div>
                <span className="text-[17px] text-black flex-1 text-left">Request Time Off</span>
                <ChevronRight className="h-5 w-5 text-gray-300" />
              </button>

              <div className="h-px bg-gray-200 ml-[58px]" />

              {/* View Paystubs */}
              <button
                onClick={() => setIsPaystubsModalOpen(true)}
                className="w-full flex items-center px-4 py-3 active:bg-gray-100 transition-colors"
              >
                <div className="h-[30px] w-[30px] rounded-[7px] bg-[#34C759] flex items-center justify-center mr-3 flex-shrink-0">
                  <FileText className="h-[17px] w-[17px] text-white" />
                </div>
                <span className="text-[17px] text-black flex-1 text-left">View Paystubs</span>
                <ChevronRight className="h-5 w-5 text-gray-300" />
              </button>
            </div>
          </div>
        )}

        {/* Earnings Report */}
        {!userInfo.isAdmin && userInfo.resourceId && (
          <div className="mt-8">
            <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-1.5">Earnings</p>
            <EarningsReport userInfo={{ 
              resourceId: userInfo.resourceId,
              firstName: userInfo.firstName,
              lastName: userInfo.lastName,
              email: userInfo.email,
              avatarUrl: userInfo.avatarUrl 
            }} />
          </div>
        )}

        {/* Time Off Requests */}
        {!userInfo.isAdmin && userInfo.resourceId && (
          <div className="mt-8">
            <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wide px-4 mb-1.5">Time Off Requests</p>
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              {isLoadingTimeOff ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-6 h-6 border-[3px] border-gray-200 border-t-[#007AFF] rounded-full animate-spin"></div>
                  <span className="ml-3 text-[15px] text-gray-500">Loading requests...</span>
                </div>
              ) : timeOffRequests.length > 0 ? (
                <div>
                  {timeOffRequests.slice(0, timeOffDisplayLimit).map((request, index) => {
                    const config = getStatusConfig(request.status);
                    const StatusIcon = config.icon;

                    return (
                      <div key={request.id}>
                        {index > 0 && <div className="h-px bg-gray-200 ml-4" />}
                        <div className="px-4 py-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0 mr-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[17px] font-medium text-black">
                                  {getTypeLabel(request.request_type)}
                                </span>
                                <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full ${config.textColor} ${config.bgColor}`}>
                                  {config.label}
                                </span>
                              </div>
                              <p className="text-[15px] text-gray-500">
                                {formatRequestDate(request.start_date)}
                                {request.start_date !== request.end_date && ` - ${formatRequestDate(request.end_date)}`}
                              </p>
                              {request.is_partial_day && request.start_time && request.end_time && (
                                <p className="text-[13px] text-gray-400 mt-0.5">
                                  {request.start_time} - {request.end_time}
                                </p>
                              )}
                              {request.notes && (
                                <p className="text-[13px] text-gray-400 mt-1 line-clamp-2">{request.notes}</p>
                              )}
                              <p className="text-[12px] text-gray-400 mt-1.5">
                                Requested {format(new Date(request.requested_at), 'MMM d, yyyy')}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                              <StatusIcon className={`h-5 w-5 ${config.textColor}`} />
                              {(request.status === 'pending' || request.status === 'approved') && (
                                <button
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to cancel this time off request?')) {
                                      handleCancelRequest(request.id);
                                    }
                                  }}
                                  className="p-1.5 text-red-400 hover:text-red-600 active:bg-red-50 rounded-lg transition-colors"
                                  title="Cancel request"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {timeOffRequests.length > timeOffDisplayLimit && (
                    <>
                      <div className="h-px bg-gray-200" />
                      <button
                        onClick={() => setTimeOffDisplayLimit(prev => prev + 5)}
                        className="w-full py-3 text-center text-[15px] font-medium text-[#007AFF] active:bg-gray-100 transition-colors"
                      >
                        Show More ({timeOffRequests.length - timeOffDisplayLimit} remaining)
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-10 px-6">
                  <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <Calendar className="h-6 w-6 text-gray-400" />
                  </div>
                  <h4 className="text-[17px] font-semibold text-black mb-1">No Requests</h4>
                  <p className="text-[15px] text-gray-500 max-w-[280px] mx-auto">
                    Tap "Request Time Off" above to submit your first request.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bottom padding for safe area */}
        <div className="h-6" />
      </div>

      {/* Time Off Request Modal */}
      {userInfo?.resourceId && (
        <TimeOffRequestModal
          isOpen={isTimeOffModalOpen}
          onClose={() => setIsTimeOffModalOpen(false)}
          onSuccess={() => {
            setIsTimeOffModalOpen(false);
            loadTimeOffRequests();
          }}
          resourceId={userInfo.resourceId}
        />
      )}

      {/* Paystubs Modal */}
      {userInfo?.resourceId && selectedBusiness?.id && (
        <ResourcePaystubsModal
          isOpen={isPaystubsModalOpen}
          onClose={() => setIsPaystubsModalOpen(false)}
          resourceId={userInfo.resourceId}
          businessId={selectedBusiness.id}
          resourceInfo={{
            name: `${userInfo.firstName} ${userInfo.lastName}`,
            email: userInfo.email,
            avatarUrl: userInfo.avatarUrl
          }}
        />
      )}
    </div>
  );
}
